import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAudienceMode } from "../audience/AudienceModeProvider";
import { env } from "../config/env";
import { useI18n } from "../i18n/useI18n";
import { AudienceModeToggle } from "./AudienceModeToggle";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ScrollToTopButton } from "./ScrollToTopButton";
import { ThemeToggle } from "./ThemeToggle";

const logoSrc = `${import.meta.env.BASE_URL}favicon.svg`;

const RAIL_COLLAPSED_KEY = "cost-analyzer:sidebar-collapsed:v1";

function readRailCollapsed(): boolean {
  try {
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem(RAIL_COLLAPSED_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function AppShell() {
  const { t } = useI18n();
  const { audienceMode, setAudienceMode } = useAudienceMode();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(RAIL_COLLAPSED_KEY, railCollapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [railCollapsed]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 899px)");
    const clearRailOnNarrow = () => {
      if (mq.matches) setRailCollapsed(false);
    };
    clearRailOnNarrow();
    mq.addEventListener("change", clearRailOnNarrow);
    return () => mq.removeEventListener("change", clearRailOnNarrow);
  }, []);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    "app-shell__link" + (isActive ? " app-shell__link--active" : "");

  function hideSidebar() {
    if (window.matchMedia("(max-width: 899px)").matches) {
      setMenuOpen(false);
    } else {
      setRailCollapsed(true);
    }
  }

  const shellClass = "app-shell" + (railCollapsed ? " app-shell--rail-collapsed" : "");

  return (
    <div className={shellClass}>
      <button
        type="button"
        className="app-shell__burger"
        aria-label={menuOpen ? t("nav.menuClose") : t("nav.menuOpen")}
        aria-expanded={menuOpen}
        aria-controls="app-sidebar"
        onClick={() => setMenuOpen((o) => !o)}
      >
        <span className="app-shell__burger-line" aria-hidden />
        <span className="app-shell__burger-line" aria-hidden />
        <span className="app-shell__burger-line" aria-hidden />
      </button>

      {menuOpen ? (
        <button
          type="button"
          className="app-shell__backdrop"
          aria-label={t("nav.menuClose")}
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <button
        type="button"
        className="app-shell__rail-expand"
        aria-label={t("nav.expandSidebar")}
        onClick={() => setRailCollapsed(false)}
      >
        <span aria-hidden>›</span>
      </button>

      <aside
        id="app-sidebar"
        className={"app-shell__sidebar" + (menuOpen ? " app-shell__sidebar--open" : "")}
        aria-label={t("nav.sidebarAria")}
        aria-hidden={railCollapsed ? true : undefined}
      >
        <div className="app-shell__sidebar-top">
          <NavLink
            to="/"
            end
            className="app-shell__brand"
            aria-label={t("nav.home")}
            onClick={() => setMenuOpen(false)}
          >
            <img src={logoSrc} width={32} height={32} alt="" decoding="async" />
            <span className="app-shell__brand-text">{env.appName}</span>
          </NavLink>
          <button
            type="button"
            className="app-shell__sidebar-hide"
            aria-label={t("nav.collapseSidebar")}
            onClick={hideSidebar}
          >
            <span aria-hidden>‹</span>
          </button>
        </div>

        <div className="app-shell__sidebar-head">
          <p className="app-shell__sidebar-tagline">{t("nav.sidebarTagline")}</p>
          <ul className="app-shell__sidebar-pills" aria-label={t("nav.sidebarStripAria")}>
            <li>{t("nav.sidebarPill1")}</li>
            <li>{t("nav.sidebarPill2")}</li>
            <li>{t("nav.sidebarPill3")}</li>
          </ul>
        </div>

        <nav className="app-shell__nav" aria-label={t("nav.footerAria")}>
          <NavLink
            to="/how-it-works"
            className={navClass}
            onClick={() => setMenuOpen(false)}
          >
            {t("nav.howItWorks")}
          </NavLink>
          <NavLink
            to="/privacy"
            className={navClass}
            onClick={() => setMenuOpen(false)}
          >
            {t("nav.privacy")}
          </NavLink>
          <NavLink to="/terms" className={navClass} onClick={() => setMenuOpen(false)}>
            {t("nav.terms")}
          </NavLink>
        </nav>

        <div className="app-shell__sidebar-view">
          <AudienceModeToggle
            variant="sidebar"
            t={t}
            value={audienceMode}
            onChange={setAudienceMode}
          />
        </div>
      </aside>

      <div className="app-shell__main">
        <div className="app-shell__toolbar">
          <div className="app-shell__toolbar-inner">
            <div className="app-shell__toolbar-lead">
              <p className="app-shell__toolbar-tagline">{t("hero.eyebrow")}</p>
              <p className="app-shell__toolbar-view-hint">{t("nav.viewModeHint")}</p>
            </div>
            <div className="app-shell__toolbar-actions">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          </div>
        </div>
        <Outlet />
      </div>

      <ScrollToTopButton />

      <style>{`
        .app-shell {
          --sidebar-rail-w: 14rem;
          --rail-tab-w: 1.65rem;
          --rail-tab-bottom: max(1rem, env(safe-area-inset-bottom, 0px) + 1rem);
          --rail-tab-gutter: 0.4rem;
          --main-content-max: 1120px;
          --main-content-pad-x: clamp(0.85rem, 4vw, 1.35rem);
          --sidebar-block-gap: 0.85rem;
          display: block;
          position: relative;
          min-height: 100vh;
          min-height: 100dvh;
        }
        .app-shell__sidebar {
          display: flex;
          flex-direction: column;
          gap: var(--sidebar-block-gap);
          padding: 0.45rem 0.45rem 0.6rem 0.5rem;
          background: var(--bg);
          border-right: 1px solid color-mix(in srgb, var(--border) 42%, var(--bg));
          transition: border-color 0.2s ease;
        }
        .app-shell__sidebar-top {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.35rem;
          min-width: 0;
        }
        .app-shell__sidebar-head {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--sidebar-block-gap);
          margin: 0;
          padding-bottom: 0;
          text-align: center;
          width: 100%;
        }
        .app-shell__sidebar-tagline {
          margin: 0;
          font-size: 0.68rem;
          line-height: 1.35;
          color: color-mix(in srgb, var(--muted) 85%, var(--text));
          font-weight: 500;
          max-width: 11.5rem;
          text-wrap: balance;
        }
        .app-shell__sidebar-pills {
          list-style: none;
          margin: 0;
          padding: 0;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: var(--sidebar-block-gap);
        }
        .app-shell__sidebar-pills li {
          font-size: 0.6rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.055em;
          color: color-mix(in srgb, var(--muted) 88%, var(--text));
          padding: 0.28rem 0.4rem;
          border-radius: 6px;
          border: 1px solid color-mix(in srgb, var(--border) 55%, transparent);
          background: color-mix(in srgb, var(--surface2) 28%, var(--bg));
          text-align: center;
          line-height: 1.2;
        }
        .app-shell__sidebar-view {
          width: 100%;
          margin-top: auto;
          padding-top: var(--sidebar-block-gap);
          border-top: 1px solid color-mix(in srgb, var(--border) 38%, var(--bg));
        }
        .app-shell__brand {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
          padding: 0.3rem 0.4rem;
          border-radius: 8px;
          text-decoration: none;
          color: var(--muted);
          font-weight: 600;
          font-size: 0.86rem;
          letter-spacing: -0.015em;
          min-width: 0;
          flex: 0 1 auto;
          transition: color 0.15s ease, background 0.15s ease;
        }
        .app-shell__brand-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .app-shell__brand img {
          border-radius: 8px;
          box-shadow: 0 0 0 1px color-mix(in srgb, var(--border) 55%, transparent);
          flex-shrink: 0;
          opacity: 0.92;
        }
        .app-shell__brand:hover {
          color: var(--text);
          background: color-mix(in srgb, var(--surface2) 55%, transparent);
        }
        .app-shell__sidebar-hide {
          flex-shrink: 0;
          width: 2rem;
          height: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid color-mix(in srgb, var(--border) 35%, transparent);
          border-radius: 8px;
          background: transparent;
          color: var(--muted);
          cursor: pointer;
          font-size: 1rem;
          line-height: 1;
          padding: 0;
          opacity: 0.85;
          -webkit-tap-highlight-color: transparent;
          transition: opacity 0.12s ease, color 0.12s ease, border-color 0.12s ease,
            background 0.12s ease;
        }
        .app-shell__sidebar-hide:hover {
          opacity: 1;
          color: var(--text);
          border-color: color-mix(in srgb, var(--border) 70%, var(--bg));
          background: color-mix(in srgb, var(--surface2) 40%, transparent);
        }
        .app-shell__nav {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: var(--sidebar-block-gap);
          text-align: center;
        }
        .app-shell__link {
          display: block;
          padding: 0.4rem 0.5rem;
          border-radius: 8px;
          border: none;
          margin-left: 0;
          color: color-mix(in srgb, var(--muted) 88%, var(--text));
          text-decoration: none;
          font-size: 0.8rem;
          font-weight: 500;
          transition:
            background 0.12s ease,
            color 0.12s ease,
            border-color 0.12s ease;
        }
        .app-shell__link:hover {
          color: var(--text);
          background: color-mix(in srgb, var(--surface2) 35%, transparent);
        }
        .app-shell__link--active {
          color: var(--text);
          font-weight: 600;
          background: color-mix(in srgb, var(--accent-chip) 45%, transparent);
          box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--accent-dim) 35%, transparent);
        }
        .app-shell__main {
          min-width: 0;
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
        }
        .app-shell__toolbar {
          flex-shrink: 0;
          padding: 0.65rem 0 0.35rem;
          background: var(--bg);
        }
        .app-shell__toolbar-inner {
          box-sizing: border-box;
          width: 100%;
          max-width: var(--main-content-max);
          margin-left: auto;
          margin-right: auto;
          padding-left: var(--main-content-pad-x);
          padding-right: var(--main-content-pad-x);
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: flex-start;
          gap: 0.55rem 0.85rem;
        }
        .app-shell__toolbar-lead {
          flex: 1 1 14rem;
          min-width: min(100%, 11rem);
          max-width: min(40rem, 100%);
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }
        .app-shell__toolbar-tagline {
          margin: 0;
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.075em;
          color: var(--accent);
          line-height: 1.35;
          text-wrap: balance;
        }
        .app-shell__toolbar-view-hint {
          margin: 0;
          font-size: 0.68rem;
          line-height: 1.4;
          font-weight: 500;
          color: color-mix(in srgb, var(--muted) 92%, var(--text));
          max-width: 38rem;
          text-wrap: balance;
        }
        .app-shell__toolbar-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: flex-end;
          gap: 0.45rem;
          flex-shrink: 0;
          align-self: center;
        }
        .app-shell__burger,
        .app-shell__backdrop,
        .app-shell__rail-expand {
          display: none;
        }
        @media (min-width: 900px) {
          .app-shell {
            --sidebar-block-gap: 0.58rem;
          }
          .app-shell__rail-expand {
            display: flex;
            align-items: center;
            justify-content: center;
            position: fixed;
            z-index: 61;
            left: 0;
            bottom: var(--rail-tab-bottom);
            top: auto;
            width: var(--rail-tab-w);
            min-height: 3.5rem;
            padding: 0;
            margin: 0;
            cursor: pointer;
            color: color-mix(in srgb, var(--accent) 85%, var(--muted));
            font-size: 1.15rem;
            font-weight: 700;
            line-height: 1;
            background: var(--bg);
            border: 1px solid color-mix(in srgb, var(--border) 55%, var(--bg));
            border-left: none;
            border-radius: 0 8px 8px 0;
            box-shadow: 2px 0 10px rgba(0, 0, 0, 0.08);
            -webkit-tap-highlight-color: transparent;
            transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
          }
          .app-shell__rail-expand:hover {
            background: color-mix(in srgb, var(--surface2) 45%, var(--bg));
            border-color: color-mix(in srgb, var(--border) 65%, var(--bg));
          }
          .app-shell__sidebar-hide {
            position: absolute;
            z-index: 2;
            left: calc(100% - var(--rail-tab-w));
            bottom: var(--rail-tab-bottom);
            top: auto;
            width: var(--rail-tab-w);
            min-height: 3.5rem;
            height: auto;
            opacity: 1;
            font-size: 1.15rem;
            font-weight: 700;
            color: color-mix(in srgb, var(--accent) 85%, var(--muted));
            background: var(--bg);
            border: 1px solid color-mix(in srgb, var(--border) 55%, var(--bg));
            border-right: none;
            border-radius: 8px 0 0 8px;
            box-shadow: -2px 0 10px rgba(0, 0, 0, 0.08);
            transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
          }
          .app-shell__sidebar-hide:hover {
            background: color-mix(in srgb, var(--surface2) 45%, var(--bg));
            border-color: color-mix(in srgb, var(--border) 65%, var(--bg));
            color: color-mix(in srgb, var(--accent) 85%, var(--muted));
          }
          .app-shell:not(.app-shell--rail-collapsed) .app-shell__rail-expand {
            display: none;
          }
          .app-shell__sidebar {
            position: fixed;
            z-index: 60;
            left: 0;
            top: 0;
            width: var(--sidebar-rail-w);
            min-height: 100vh;
            min-height: 100dvh;
            box-sizing: border-box;
            justify-content: flex-start;
            gap: var(--sidebar-block-gap);
            padding-top: 0.65rem;
            padding-left: 0.65rem;
            padding-right: calc(var(--rail-tab-w) + 0.5rem);
            padding-bottom: max(0.85rem, env(safe-area-inset-bottom, 0px) + 0.35rem);
            transform: translateX(0);
            transition: transform 0.24s cubic-bezier(0.32, 0.72, 0, 1);
          }
          .app-shell__sidebar-view {
            margin-bottom: calc(var(--rail-tab-bottom) + 3.6rem);
          }
          .app-shell__sidebar-tagline {
            max-width: none;
          }
          .app-shell--rail-collapsed .app-shell__sidebar {
            transform: translateX(-100%);
            pointer-events: none;
          }
          .app-shell__main {
            margin-left: var(--sidebar-rail-w);
            padding-left: 0;
            transition:
              margin-left 0.24s cubic-bezier(0.32, 0.72, 0, 1),
              padding-left 0.24s cubic-bezier(0.32, 0.72, 0, 1);
          }
          .app-shell--rail-collapsed .app-shell__main {
            margin-left: 0;
            padding-left: calc(var(--rail-tab-w) + var(--rail-tab-gutter));
          }
        }
        @media (max-width: 899px) {
          .app-shell__burger {
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 5px;
            position: fixed;
            z-index: 210;
            top: max(0.65rem, env(safe-area-inset-top, 0px));
            left: max(0.65rem, env(safe-area-inset-left, 0px));
            width: 2.75rem;
            height: 2.75rem;
            padding: 0;
            border-radius: 8px;
            border: 1px solid var(--border);
            background: var(--surface);
            cursor: pointer;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
          }
          .app-shell__burger-line {
            display: block;
            width: 1.1rem;
            height: 2px;
            margin: 0 auto;
            background: var(--text);
            border-radius: 1px;
          }
          .app-shell__backdrop {
            display: block;
            position: fixed;
            inset: 0;
            z-index: 198;
            border: none;
            padding: 0;
            margin: 0;
            background: rgba(0, 0, 0, 0.38);
            cursor: pointer;
          }
          .app-shell__sidebar {
            position: fixed;
            z-index: 200;
            top: 0;
            left: 0;
            width: min(17.5rem, 88vw);
            min-height: 100vh;
            min-height: 100dvh;
            transform: translateX(-100%);
            transition: transform 0.22s ease;
            box-shadow: 4px 0 24px rgba(0, 0, 0, 0.2);
            justify-content: flex-start;
            gap: var(--sidebar-block-gap);
            padding-top: max(1.15rem, env(safe-area-inset-top, 0px) + 0.2rem);
            padding-bottom: max(1.1rem, env(safe-area-inset-bottom, 0px) + 0.5rem);
          }
          .app-shell__sidebar-top {
            justify-content: space-between;
            align-items: center;
            padding-right: 0;
          }
          .app-shell__brand {
            flex: 1;
            justify-content: flex-start;
          }
          .app-shell__sidebar-hide {
            position: static;
          }
          .app-shell__sidebar-head {
            text-align: left;
            align-items: stretch;
          }
          .app-shell__sidebar-tagline {
            max-width: none;
            line-height: 1.45;
          }
          .app-shell__sidebar-pills li {
            text-align: left;
            padding: 0.36rem 0.5rem;
          }
          .app-shell__nav {
            text-align: left;
          }
          .app-shell__link {
            text-align: left;
            padding: 0.52rem 0.55rem;
          }
          .app-shell__link--active {
            box-shadow: none;
            border-left: 2px solid color-mix(in srgb, var(--accent) 75%, transparent);
            background: color-mix(in srgb, var(--accent-chip) 35%, transparent);
          }
          .app-shell__sidebar--open {
            transform: translateX(0);
          }
          .app-shell__main {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
